import React from 'react';
import { Typography, Stack, styled } from '@mui/material';
import { ResponsiveSVG } from 'components/ResponsiveSVG/ResponsiveSVG';
import NotFoundImage from 'assets/images/ErrorState/UnexpectedErrorTwo.svg';
import { Button } from 'components/Button/Button';
import { useAssessmentsPaths } from 'features/assessments/hooks/useAssessmentsPaths';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { ERROR_TEXT } from 'features/assessments/constants';

interface AssessmentsNotFoundProps {
  hideBackHomeButton?: boolean;
  backHomeButtonLabel?: string;
}

const MainContainer = styled(Stack)(({ theme }) => ({
  height: '100vh',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(6),
}));

const ContentContainer = styled(Stack)(({ theme }) => ({
  maxWidth: theme.spacing(60),
  width: '100%',
  textAlign: 'center',
  gap: theme.spacing(1),
}));

const ErrorDescription = styled(Typography)(({ theme }) => ({
  whiteSpace: 'pre-line',
  textAlign: 'center',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  lineHeight: 1.5,
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.body1.fontSize,
  },
}));

export const AssessmentsNotFound: React.FC<AssessmentsNotFoundProps> = ({
  hideBackHomeButton = false,
  backHomeButtonLabel,
}) => {
  const { assessementsVerifyPage } = useAssessmentsPaths();
  const navigate = useNavigate();
  const { translation } = useTranslationContext();

  const handleBackHomeClick = () => {
    navigate(assessementsVerifyPage);
  };

  return (
    <MainContainer role="main" aria-labelledby="assessments-not-found-title">
      <ResponsiveSVG
        svg={NotFoundImage}
        deviceWidth={{ desktop: '315px', tablet: '315px', mobile: '252px' }}
        alt="Page not found illustration"
      />
      <ContentContainer>
        <Typography
          id="assessments-not-found-title"
          fontWeight={500}
          align="center"
          variant="extraLargeNumber"
          color="primary"
        >
          404
        </Typography>
        <ErrorDescription align="center" variant="title3" color="grey.800">
          {ERROR_TEXT}
        </ErrorDescription>
      </ContentContainer>
      {hideBackHomeButton ? null : (
        <Button variant="tertiary" onClick={handleBackHomeClick} aria-label="Back Home">
          {backHomeButtonLabel ?? translation('four_zero_four_error_back_home')}
        </Button>
      )}
    </MainContainer>
  );
};
